import { apiClient } from "@apis/apiClient";
import UTILS from "../apiUltils";
import { saveAs } from "file-saver";
import type {
  Area,
  AreaParams,
  BinsConfig,
  AreaSearchParams,
  GeozoneStatisticsParams,
  CountGeozonesByTagIdsParams,
  Country,
  ImportGeozonesParams,
  ApiResponse,
} from "./areaClient.dto";

/**
 * Area (Geozone) API Client
 *
 * Provides methods for managing geozones including CRUD operations,
 * search, statistics, import/export, and country data.
 * Uses DTOs from areaClient.dto.ts for type safety.
 */
const areaClient = {
  /**
   * Create a new area/geozone
   * @param data - Area creation parameters
   * @param orgId - Organization ID
   * @returns Created area
   */
  addArea(data: AreaParams, orgId: number): Promise<ApiResponse<Area>> {
    return apiClient.post(`/rest/area?orgId=${orgId}`, data, {
      headers: { "content-type": "application/json" },
    });
  },

  /**
   * Validate area data before creation/update
   * @param data - Area parameters to validate
   * @param orgId - Organization ID
   * @returns Validation response
   */
  validate(
    data: AreaParams,
    orgId: number
  ): Promise<ApiResponse<{ valid: boolean }>> {
    return apiClient.post(`/rest/area/validate?orgId=${orgId}`, data, {
      headers: { "content-type": "application/json" },
    });
  },

  /**
   * Update an existing area/geozone
   * @param data - Area update parameters
   * @param id - Area ID
   * @returns Updated area
   */
  updateArea(data: AreaParams, id: string): Promise<ApiResponse<Area>> {
    return apiClient.put(`/rest/area/${id}`, data, {
      headers: { "content-type": "application/json" },
    });
  },

  /**
   * Update bins configuration for an area
   * @param id - Area ID
   * @param data - Bins configuration
   * @returns Updated bins config
   */
  updateBinsConfigForArea(
    id: string,
    data: BinsConfig
  ): Promise<ApiResponse<BinsConfig>> {
    return apiClient.put(`/rest/area/bins/config/${id}`, data, {
      headers: { "content-type": "application/json" },
    });
  },

  /**
   * Search areas with filter criteria
   * @param data - Search parameters
   * @param cancelToken - Optional axios cancel token for request cancellation
   * @returns Array of matching areas
   */
  search(
    data: AreaSearchParams,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cancelToken?: any
  ): Promise<ApiResponse<Area[]>> {
    return apiClient.post("/rest/area/search", data, {
      headers: { "content-type": "application/json" },
      cancelToken: cancelToken,
    });
  },

  /**
   * Perform double search on areas
   * @param data - Search parameters
   * @param cancelToken - Optional axios cancel token for request cancellation
   * @returns Array of matching areas
   */
  doubleSearch(
    data: AreaSearchParams,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cancelToken?: any
  ): Promise<ApiResponse<Area[]>> {
    return apiClient.post("/rest/area/doublesearch", data, {
      headers: { "content-type": "application/json" },
      cancelToken: cancelToken,
    });
  },

  /**
   * Get statistics for a specific geozone
   * @param data - Statistics query parameters
   * @param cancelToken - Optional axios cancel token for request cancellation
   * @returns Geozone statistics
   */
  getStatisticsByGeozone(
    data: GeozoneStatisticsParams,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cancelToken?: any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Promise<ApiResponse<any>> {
    return apiClient.post("/rest/area/statistics/bygeozone", data, {
      headers: { "content-type": "application/json" },
      cancelToken: cancelToken,
    });
  },

  /**
   * Get statistics grouped by geozone tag
   * @param data - Statistics query parameters
   * @param cancelToken - Optional axios cancel token for request cancellation
   * @returns Statistics grouped by geozone tag
   */
  getStatisticsByGeozoneTag(
    data: GeozoneStatisticsParams,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cancelToken?: any
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Promise<ApiResponse<any>> {
    return apiClient.post("/rest/area/statistics/bygeozonetag", data, {
      headers: { "content-type": "application/json" },
      cancelToken: cancelToken,
    });
  },

  /**
   * Get all areas
   * @returns Array of all areas
   */
  findAll(): Promise<ApiResponse<Area[]>> {
    return apiClient.get(`/rest/area/`);
  },

  /**
   * Get all areas by organization ID
   * @param orgId - Organization ID (optional)
   * @returns Array of areas
   */
  findAllByOrgId(orgId?: number): Promise<ApiResponse<Area[]>> {
    return apiClient.get(`/rest/area${orgId ? "?orgId=" + orgId : ""}`);
  },

  /**
   * Get a specific area by ID
   * @param id - Area ID
   * @returns Area details
   */
  getAreaById(id: string): Promise<ApiResponse<Area>> {
    return apiClient.get(`/rest/area/${id}`);
  },

  /**
   * Get areas by organization IDs
   * @param orgId - Organization ID or comma-separated IDs (optional)
   * @returns Array of areas
   */
  getAreaByOrgs(orgId?: string): Promise<ApiResponse<Area[]>> {
    return apiClient.get(
      `/rest/area/getAreaByOrgs${orgId ? "?orgIds=" + orgId : ""}`
    );
  },

  /**
   * Count geozones by tag IDs
   * @param params - Count parameters with tag IDs
   * @returns Count response
   */
  countGeozonesByTagIds(
    params: CountGeozonesByTagIdsParams
  ): Promise<ApiResponse<{ count: number }>> {
    const uri = encodeURI(
      UTILS.buildParamsListURI("/rest/area/countGeozonesByTagIds", params)
    );
    return apiClient.get(uri);
  },

  /**
   * Get a specific area (alias for getAreaById)
   * @param areaId - Area ID
   * @returns Area details
   */
  getArea(areaId: string): Promise<ApiResponse<Area>> {
    return apiClient.get(`/rest/area/${areaId}`);
  },

  /**
   * Get a specific area by name using search
   * @param name - Area name (exact match)
   * @returns Area details or null if not found
   */
  getAreaByName(name: string): Promise<ApiResponse<Area | null>> {
    return apiClient
      .get(
        `/rest/area?search=${encodeURIComponent(name)}&exactMatch=true&start=0&limit=1`
      )
      .then((response) => {
        const result = response.data as { data: Area[]; count: number };
        const areas = result?.data || [];
        return {
          ...response,
          data: areas.length > 0 ? areas[0] : null,
        };
      });
  },

  /**
   * Download CSV template for geozones
   * @returns Promise that resolves when download completes
   */
  downloadCsvTemplate(): Promise<void> {
    return apiClient
      .post("/rest/area/download/csvTemplateFile", "", {
        responseType: "arraybuffer",
      })
      .then((response) => {
        const blob = new Blob([response.data]);
        saveAs(blob, `Geozones_Template.csv`);
      });
  },

  /**
   * Download Excel template for geozones
   * @returns Promise that resolves when download completes
   */
  downloadXlsTemplate(): Promise<void> {
    return apiClient
      .post("/rest/area/download/xlsTemplateFile", "", {
        responseType: "arraybuffer",
      })
      .then((response) => {
        const blob = new Blob([response.data], {
          type:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        saveAs(blob, `Geozones_Template.xlsx`);
      });
  },

  /**
   * Export geozones as CSV file
   * @param orgId - Organization ID
   * @param data - Export parameters
   * @returns Promise that resolves when download completes
   */
  exportAsCsvFile(
    orgId: number,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any
  ): Promise<void> {
    return apiClient
      .post(`/rest/area/download/csvTemplateFile?orgId=${orgId}`, data, {
        responseType: "arraybuffer",
      })
      .then((response) => {
        const blob = new Blob([response.data]);
        saveAs(blob, `Geozones_By_Org_${orgId}.csv`);
      });
  },

  /**
   * Export geozones as Excel file
   * @param orgId - Organization ID
   * @param data - Export parameters
   * @returns Promise that resolves when download completes
   */
  exportAsXlsFile(
    orgId: number,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    data: any
  ): Promise<void> {
    return apiClient
      .post(`/rest/area/download/xlsTemplateFile?orgId=${orgId}`, data, {
        responseType: "arraybuffer",
      })
      .then((response) => {
        const blob = new Blob([response.data], {
          type:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
        saveAs(blob, `Geozones_By_Org_${orgId}.xlsx`);
      });
  },

  /**
   * Import geozones from file
   * @param orgId - Organization ID
   * @param params - FormData with file
   * @returns Import response
   */
  importGeozonesByFile(
    orgId: number,
    params: ImportGeozonesParams
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ): Promise<ApiResponse<any>> {
    return apiClient.post(`/rest/area/import?orgId=${orgId}`, params, {
      headers: { "content-type": "multipart/form-data" },
    });
  },

  /**
   * Get list of all countries
   * @returns Array of countries
   */
  getCountries(): Promise<ApiResponse<Country[]>> {
    return apiClient.get(`/rest/area/countries`, {});
  },

  /**
   * Get country by ISO code
   * @param isoCode - Country ISO code
   * @returns Country details
   */
  getCountryByIsoCode(isoCode: string): Promise<ApiResponse<Country>> {
    return apiClient.get(`/rest/area/country/byIsoCode/${isoCode}`, {});
  },
};

export default areaClient;
